import { erroresPorCampo, traducirError, useTraduccion, type ProblemaApi } from '@tallerya/i18n';
import {
  AvisoError,
  Boton,
  CampoAreaTexto,
  CampoNumero,
  CampoSeleccion,
  CampoTexto,
  Fila,
  IconoMas,
  useAvisos,
  useFormulario,
  Ventana,
  type EstadoFormulario,
  type OpcionCampo,
} from '@tallerya/ui';
import { useId, useRef, useState, type ChangeEvent } from 'react';
import { Adjuntos } from '../../componentes/adjuntos/Adjuntos';
import { subirAdjunto, TIPOS_ACEPTADOS } from '../../componentes/adjuntos/datos';
import { usePermisos } from '../../permisos/usePermiso';
import { numeroDe } from '../productos-servicios/datos';
import { useClaveDeReintento } from '../ordenes/datos';
import { Pie } from '../ordenes/VentanasDeLinea';
import { errorDeMonto } from '../ordenes/VentanasDeMontos';
import type { ProveedorCreado } from '../proveedores/datos';
import { VentanaProveedor } from '../proveedores/VentanaProveedor';
import {
  hoy,
  useCambiosGasto,
  useFichaGasto,
  useProveedoresParaGasto,
  version,
  type EnvioGasto,
  type FichaGasto,
} from './datos';

// Registrar y editar un gasto (#143). No tiene frame en el Figma —solo la fila de
// «Gastos de esta orden» y la ventana del proveedor al vuelo—: sigue a las
// ventanas de la orden, porque se abre sobre su ficha, o sobre el listado de la
// caja (#144), y no se sale de ellas.
//
//   Registrar gasto ⓘ
//   Descripción *
//   Fecha de compra *        Proveedor ▾  (… + Nuevo proveedor)
//   N.º de factura           Monto
//   Notas
//   Comprobante                                    [+ Agregar]
//
// - **Con la descripción y la fecha alcanza**: lo demás lo completa después
//   quien lleva la caja. Es la regla del mecánico, aunque acá escriba el admin.
// - **El proveedor que no existe se crea encima**, con `VentanaProveedor`, y
//   queda elegido. Lo escrito en el gasto no se toca: la ventana no navega.
// - **Al registrar, el monto y la foto van después del gasto**: el monto tiene su
//   endpoint y su permiso, y la foto necesita el id. Son hasta tres pedidos.
// - **Al editar no se cambia el monto**: va por su ventana, «Cambiar monto», con
//   la versión que leyó, como el precio de una línea. El comprobante sí está:
//   la galería sube y quita en el momento.

/** Lo que edita el formulario. Los nombres son los del contrato: los errores por campo llegan con esas claves. */
interface ValoresGasto {
  description: string;
  purchasedOn: string;
  supplierId: string;
  documentNumber: string;
  notes: string;
  amount: string;
}

/** Los errores de la API que son de un campo, aunque no lleguen por campo. */
const CAMPOS_POR_CODIGO = {
  'expense.factura_repetida': 'documentNumber',
  'expense.proveedor_no_encontrado': 'supplierId',
  'expense.proveedor_apagado': 'supplierId',
  'expense.pago_antes_de_compra': 'purchasedOn',
} as const;

export interface DestinoGasto {
  locationId: string;
  serviceOrderId: string | null;
}

interface Props {
  /**
   * Adónde va un gasto nuevo: la sucursal, y la orden si es de una. Desde la
   * ficha, la orden y su sucursal; desde el listado (#144), la sucursal del app
   * bar y sin orden, que es un gasto del taller: la renta, la luz.
   */
  destino: DestinoGasto;
  /** `null`: cerrada. `'nuevo'`: el alta. Un id: la edición de ese gasto. */
  gasto: 'nuevo' | string | null;
  /** Lo que no se pudo al registrarlo, para decirlo fijo en la edición. */
  pendiente?: string | undefined;
  onCerrar: () => void;
  /** Pasa la ventana a la edición del gasto recién registrado, con lo que le faltó. */
  onEditar: (id: string, pendiente: string) => void;
}

export function VentanaGasto({ gasto, pendiente, ...props }: Props) {
  if (gasto === null) return null;
  // La clave reinicia la ventana al pasar del alta a la edición.
  return gasto === 'nuevo' ? (
    <Alta key="nuevo" {...props} />
  ) : (
    <Edicion key={gasto} id={gasto} pendiente={pendiente} onCerrar={props.onCerrar} />
  );
}

// ------------------------------------------------------------ El alta

function Alta({ destino, onCerrar, onEditar }: Omit<Props, 'gasto' | 'pendiente'>) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const { puede } = usePermisos();
  const cambios = useCambiosGasto();
  const claveDe = useClaveDeReintento();
  const conMonto = puede('expense.amount.view') && puede('expense.amount.edit');
  const [archivos, setArchivos] = useState<File[]>([]);

  /** El gasto ya registrado: si algo de después falla, no se registra dos veces. */
  const creado = useRef<string | null>(null);
  /** Lo que no se pudo después de registrarlo, ya traducido: el monto, o alguna foto. */
  const fallas = useRef<string[]>([]);
  /** Lo que faltó, para quien no puede pasar a la edición: queda fijo hasta que cierre. */
  const [incompleto, setIncompleto] = useState<string | null>(null);

  const textoDe = useTextoDeProblema();

  /** El monto y las fotos, sobre el gasto ya registrado. Junta lo que falle sin cortar. */
  async function completar(id: string, monto: number | null): Promise<string[]> {
    const fallo: string[] = [];

    if (monto !== null) {
      try {
        const leido = await cambios.leerMonto(id);
        const problema = await cambios.cambiarMonto(crypto.randomUUID(), id, version(leido.version), monto);
        if (problema) fallo.push(`${t('admin.gastos.formulario.monto')}: ${textoDe(problema)} ${t('admin.gastos.alta.montoDespues')}`);
      } catch {
        fallo.push(
          `${t('admin.gastos.formulario.monto')}: ${t('ui.formulario.aviso.sinRespuesta')} ${t('admin.gastos.alta.montoDespues')}`,
        );
      }
    }

    // De a una, como la galería: varias fotos grandes en paralelo se pelean la conexión.
    for (const archivo of archivos) {
      try {
        const problema = await subirAdjunto({ tipo: 'gasto', id }, archivo);
        if (problema) fallo.push(`${archivo.name}: ${textoDe(problema)}`);
      } catch {
        fallo.push(`${archivo.name}: ${t('ui.formulario.aviso.sinRespuesta')}`);
      }
    }

    return fallo;
  }

  const form = useFormulario<ValoresGasto>({
    inicial: { description: '', purchasedOn: hoy(), supplierId: '', documentNumber: '', notes: '', amount: '' },
    validar: (v) => (conMonto ? errorDeMonto('amount', v.amount) : {}),
    guardar: async (v) => {
      if (!creado.current) {
        const envio = envioDe(v, destino.locationId, destino.serviceOrderId);
        const { id, problema } = await cambios.registrar(claveDe(envio), envio);
        if (!id) return problema;
        creado.current = id;
      }
      fallas.current = await completar(creado.current, conMonto ? numeroDe(v.amount) : null);
    },
    camposPorCodigo: CAMPOS_POR_CODIGO,
  });

  async function registrar() {
    if (!(await form.enviar()) || !creado.current) return;

    if (fallas.current.length === 0) {
      avisar({ titulo: t('admin.gastos.aviso.registrado') });
      onCerrar();
      return;
    }
    const faltante = fallas.current.join(' · ');
    // Quien no edita gastos —recepción, el mecánico— no podría guardar esa edición:
    // queda en el alta con el aviso rojo y solo «Cerrar». No el aviso verde: tiene
    // el ✓ de lo que salió bien y se va en segundos.
    if (!puede('expense.edit')) {
      setIncompleto(faltante);
      return;
    }
    // A medias, a la edición del gasto nuevo y no al alta: ahí se ve el comprobante
    // y se reintenta solo lo que faltó. Otro «Registrar» volvería a subir todas las fotos.
    // Lo que faltó queda fijo arriba de la edición: el aviso verde se va en segundos.
    onEditar(creado.current, faltante);
  }

  if (incompleto) {
    return (
      <Ventana
        abierta
        ancho={560}
        titulo={t('admin.gastos.formulario.nuevo')}
        acciones={<Boton onClick={onCerrar}>{t('admin.gastos.alta.cerrar')}</Boton>}
      >
        <AvisoError aviso={{ titulo: t('admin.gastos.alta.incompleto'), texto: incompleto }} />
      </Ventana>
    );
  }

  return (
    <Ventana
      abierta
      ancho={560}
      titulo={t('admin.gastos.formulario.nuevo')}
      info={t('admin.gastos.formulario.info')}
      acciones={
        <Pie
          guardando={form.guardando}
          etiqueta={t('admin.gastos.registrar')}
          onCerrar={onCerrar}
          onGuardar={() => void registrar()}
        />
      }
    >
      <Campos form={form} conMonto={conMonto} />
      <FotosPorSubir archivos={archivos} onCambiar={setArchivos} deshabilitado={form.guardando} />
    </Ventana>
  );
}

/** Las fotos elegidas en el alta: se suben cuando el gasto ya tiene id. */
function FotosPorSubir({
  archivos,
  onCambiar,
  deshabilitado,
}: {
  archivos: File[];
  onCambiar: (archivos: File[]) => void;
  deshabilitado: boolean;
}) {
  const { t } = useTraduccion();
  const selector = useRef<HTMLInputElement>(null);
  const idTitulo = useId();

  function elegir(evento: ChangeEvent<HTMLInputElement>) {
    const nuevos = [...(evento.target.files ?? [])];
    // Se limpia para que volver a elegir el mismo archivo dispare el cambio.
    evento.target.value = '';
    if (nuevos.length > 0) onCambiar([...archivos, ...nuevos]);
  }

  return (
    <section className="adjuntos gasto__fotos" aria-labelledby={idTitulo}>
      <header className="adjuntos__encabezado">
        <h2 id={idTitulo}>{t('admin.gastos.comprobante.titulo')}</h2>
        <Boton
          variante="secundario"
          icono={<IconoMas />}
          disabled={deshabilitado}
          onClick={() => selector.current?.click()}
        >
          {t('admin.adjuntos.agregar')}
        </Boton>
        <input ref={selector} type="file" accept={TIPOS_ACEPTADOS} multiple hidden onChange={elegir} />
      </header>
      {archivos.length === 0 ? (
        <p className="adjuntos__nota">{t('admin.gastos.comprobante.ninguno')}</p>
      ) : (
        <ul className="gasto__archivos">
          {archivos.map((archivo, i) => (
            <li key={`${archivo.name}-${String(i)}`}>
              <span>{archivo.name}</span>
              <Boton
                variante="texto"
                disabled={deshabilitado}
                aria-label={t('admin.gastos.comprobante.quitar', { nombre: archivo.name })}
                onClick={() => onCambiar(archivos.filter((_, j) => j !== i))}
              >
                ×
              </Boton>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ------------------------------------------------------------ La edición

function Edicion({ id, pendiente, onCerrar }: { id: string; pendiente?: string | undefined; onCerrar: () => void }) {
  const { t } = useTraduccion();
  const ficha = useFichaGasto(id);
  // Cuántas veces se recargó con «Recargar» (#172): es la clave del editor, no la
  // versión de la ficha. Así otra versión que llegue mientras se escribe no borra
  // lo escrito en silencio: al guardar, la API responde el cambio simultáneo, y
  // la persona decide recargar.
  const [lectura, setLectura] = useState(0);

  // El editor congela la ficha con que arranca: si la de la caché se está
  // recargando al abrir —quedó vieja tras «Cambiar monto» o «Marcar pagado», que
  // comparten la versión—, se espera esa primera lectura. Arrancar con la vieja
  // daría un cambio simultáneo contra un cambio propio.
  const esperandoPrimeraLectura = ficha.isFetching && !ficha.isFetchedAfterMount;

  // Solo sin datos se cambia el formulario por el mensaje. Con datos, una recarga
  // en segundo plano que falla —al volver la señal, por ejemplo— deja la ficha que
  // ya se tenía, y lo escrito sigue ahí (#172).
  if (ficha.data === undefined || ficha.data === null || esperandoPrimeraLectura) {
    return (
      <Ventana
        abierta
        ancho={560}
        titulo={t('admin.gastos.formulario.editar')}
        acciones={
          <Boton variante="texto" onClick={onCerrar}>
            {t('app.cancelar')}
          </Boton>
        }
      >
        <p className="ficha__nota">
          {esperandoPrimeraLectura
            ? t('app.cargando')
            : ficha.data === null
              ? t('error.expense.no_encontrado')
              : ficha.isError
                ? t('ui.listado.error.texto')
                : t('app.cargando')}
        </p>
      </Ventana>
    );
  }

  // Recargar empieza de cero con la ficha nueva: `useFormulario` lee lo inicial una vez.
  return (
    <Editor
      key={`${id}-${String(lectura)}`}
      fichaAlAbrir={ficha.data}
      pendiente={pendiente}
      onCerrar={onCerrar}
      recargar={async () => {
        // `refetch` no lanza al fallar: sin la ficha nueva, se queda lo escrito y el aviso.
        const nueva = await ficha.refetch();
        if (nueva.isError) return;
        setLectura((n) => n + 1);
      }}
    />
  );
}

function Editor({
  fichaAlAbrir,
  pendiente,
  onCerrar,
  recargar,
}: {
  fichaAlAbrir: FichaGasto;
  pendiente?: string | undefined;
  onCerrar: () => void;
  recargar: () => Promise<void>;
}) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosGasto();
  const claveDe = useClaveDeReintento();
  // La ficha con que se abrió, quieta aunque la consulta traiga otra: su versión es
  // la que se manda al guardar. Una versión nueva se ve solo con «Recargar».
  const [ficha] = useState(fichaAlAbrir);
  const id = String(ficha.id);

  const form = useFormulario<ValoresGasto>({
    inicial: {
      description: ficha.description,
      purchasedOn: ficha.purchasedOn,
      supplierId: ficha.supplierId ? String(ficha.supplierId) : '',
      documentNumber: ficha.documentNumber ?? '',
      notes: ficha.notes ?? '',
      amount: '',
    },
    guardar: (v) => {
      // La sucursal y la orden no se editan acá: se mandan las que tiene.
      const envio = envioDe(v, String(ficha.locationId), ficha.serviceOrderId ? String(ficha.serviceOrderId) : null);
      const versionLeida = version(ficha.version);
      return cambios.editar(claveDe({ versionLeida, envio }), id, versionLeida, envio);
    },
    camposPorCodigo: CAMPOS_POR_CODIGO,
    recargar: () => void recargar(),
  });

  async function guardar() {
    if (!(await form.enviar())) return;
    avisar({ titulo: t('admin.gastos.aviso.guardado') });
    onCerrar();
  }

  // Un proveedor desactivado después sigue siendo el de este gasto: se ofrece aunque
  // el combo no lo traiga, o el campo se vería vacío y al guardar se perdería.
  const actual =
    ficha.supplierId && ficha.supplierName ? { valor: String(ficha.supplierId), etiqueta: ficha.supplierName } : null;

  return (
    <Ventana
      abierta
      ancho={560}
      titulo={t('admin.gastos.formulario.editar')}
      info={t('admin.gastos.formulario.info')}
      descripcion={ficha.createdByName ? t('admin.gastos.formulario.registradoPor', { persona: ficha.createdByName }) : undefined}
      acciones={<Pie guardando={form.guardando} onCerrar={onCerrar} onGuardar={() => void guardar()} />}
    >
      {pendiente && <AvisoError aviso={{ titulo: t('admin.gastos.alta.incompleto'), texto: pendiente }} />}
      <Campos form={form} conMonto={false} proveedorActual={actual} />
      <div className="gasto__fotos">
        <Adjuntos origen={{ tipo: 'gasto', id }} titulo={t('admin.gastos.comprobante.titulo')} />
      </div>
    </Ventana>
  );
}

// ------------------------------------------------------------ Lo que comparten

/** El cuerpo del contrato a partir de lo escrito. */
function envioDe(v: ValoresGasto, locationId: string, serviceOrderId: string | null): EnvioGasto {
  return {
    locationId,
    serviceOrderId,
    description: v.description,
    purchasedOn: v.purchasedOn,
    supplierId: v.supplierId,
    documentNumber: v.documentNumber,
    notes: v.notes,
  };
}

/** El texto de un rechazo: el del campo si la API señaló uno, si no el general. */
function useTextoDeProblema() {
  const { t } = useTraduccion();
  return (problema: ProblemaApi): string => {
    const [primero] = Object.values(erroresPorCampo(problema, t)).flat();
    if (primero) return primero;
    return problema.code ? traducirError(problema.code, t) : t('ui.formulario.aviso.sinRespuesta');
  };
}

function Campos({
  form,
  conMonto,
  proveedorActual = null,
}: {
  form: EstadoFormulario<ValoresGasto>;
  conMonto: boolean;
  proveedorActual?: OpcionCampo | null;
}) {
  const { t } = useTraduccion();
  const { puede } = usePermisos();

  return (
    <>
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoTexto
        form={form}
        nombre="description"
        etiqueta={t('admin.gastos.formulario.descripcion')}
        placeholder={t('admin.gastos.formulario.descripcionEjemplo')}
        obligatorio
        ancho="completo"
        autoComplete="off"
      />
      <Fila>
        <CampoTexto
          form={form}
          nombre="purchasedOn"
          etiqueta={t('admin.gastos.formulario.fecha')}
          tipo="date"
          obligatorio
        />
        {/* Listar y crear proveedores pide `supplier.edit`, que recepción y el mecánico
            no tienen: registran sin proveedor y lo completa después quien lleva la caja.
            Sin el campo, el proveedor que ya tenía el gasto viaja igual en lo editado. */}
        {puede('supplier.edit') && <CampoProveedor form={form} proveedorActual={proveedorActual} />}
      </Fila>
      <Fila>
        <CampoTexto
          form={form}
          nombre="documentNumber"
          etiqueta={t('admin.gastos.formulario.factura')}
          autoComplete="off"
        />
        {conMonto && (
          <CampoNumero
            form={form}
            nombre="amount"
            etiqueta={t('admin.gastos.formulario.monto')}
            info={t('admin.gastos.formulario.montoInfo')}
            decimales={6}
          />
        )}
      </Fila>
      <CampoAreaTexto form={form} nombre="notes" etiqueta={t('admin.gastos.formulario.notas')} ancho="completo" />
    </>
  );
}

/** El combo de proveedor, con «Nuevo proveedor» al pie que abre la ventana encima. */
function CampoProveedor({
  form,
  proveedorActual,
}: {
  form: EstadoFormulario<ValoresGasto>;
  proveedorActual: OpcionCampo | null;
}) {
  const { t } = useTraduccion();
  const proveedores = useProveedoresParaGasto();
  const [creando, setCreando] = useState(false);
  /** El creado al vuelo, por si la lista todavía no lo trae: la recarga pudo fallar. */
  const [creado, setCreado] = useState<OpcionCampo | null>(null);

  const opciones: OpcionCampo[] = (proveedores.data ?? []).map((p) => ({ valor: String(p.id), etiqueta: p.name }));
  for (const extra of [proveedorActual, creado]) {
    if (extra && !opciones.some((o) => o.valor === extra.valor)) opciones.push(extra);
  }

  function alCrear(proveedor: ProveedorCreado) {
    setCreado({ valor: proveedor.id, etiqueta: proveedor.name });
    form.cambiar('supplierId', proveedor.id);
  }

  return (
    <>
      <CampoSeleccion
        form={form}
        nombre="supplierId"
        etiqueta={t('admin.gastos.formulario.proveedor')}
        placeholder={t('admin.gastos.formulario.sinProveedor')}
        opciones={opciones}
        accion={{ etiqueta: t('admin.gastos.formulario.nuevoProveedor'), onSeleccionar: () => setCreando(true) }}
      />
      <VentanaProveedor abierta={creando} onCerrar={() => setCreando(false)} onCreado={alCrear} />
    </>
  );
}
